/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

/**
 * A pool of reusable objects.
 *
 * Use it in hot code to avoid garbage. Call `get()` to claim an object. Call
 * `release()` when the object is not in use any more. A pool never grows
 * beyond `MAX_POOL_SIZE`. Extra objects go to the garbage collector.
 *
 * Upstream debug counters, timers and log calls are removed. The engine has
 * no clock and no logger.
 */
export class Pool<T extends object> {
  private static MAX_POOL_SIZE = 100;

  private readonly pool: T[] = [];
  private readonly poolable: IPoolable<T>;

  constructor(poolable: IPoolable<T>) {
    this.poolable = poolable;
  }

  public get(): T {
    const recycled = this.pool.pop();
    const obj = recycled ?? new this.poolable();
    (obj as PoolMarked).__pool = true;
    return obj;
  }

  public release(obj: T): void {
    const marked = obj as PoolMarked;
    if (!marked.__pool) {
      // The object did not come from the pool. Ignore it.
      return;
    }
    // Clear the mark. A second release of the same object is then ignored,
    // so the pool never hands out one object twice.
    marked.__pool = false;
    if (this.pool.length >= Pool.MAX_POOL_SIZE) {
      // Pool is full. Leave the object to the garbage collector.
      return;
    }
    if (this.poolable.reset) {
      this.poolable.reset(obj);
    }
    this.pool.push(obj);
  }
}

interface PoolMarked {
  __pool?: boolean;
}

export interface IPoolable<T> {
  // constructor
  new (): T;

  // static
  reset?(obj: T): void;
}
